<script>
    import { onMount } from 'svelte';
    
    // Component state
    let students = [];
    let familyGroups = {};
    let studentsWithoutFamily = [];
    let loading = false;
    let error = '';
    let searchTerm = '';
    let showGrouped = true; // Toggle between grouped and flat view
    
    // Filtered students based on search
    $: filteredStudents = students.filter(student => 
        student.first_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        student.last_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        student.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
        student.username.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (student.parent && (
            student.parent.first_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
            student.parent.last_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
            student.parent.email.toLowerCase().includes(searchTerm.toLowerCase())
        ))
    );
    
    // Filtered family groups based on search
    $: filteredFamilyGroups = Object.entries(familyGroups).reduce((acc, [familyName, familyStudents]) => {
        const filteredFamilyStudents = familyStudents.filter(student => 
            student.first_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
            student.last_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
            student.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
            student.username.toLowerCase().includes(searchTerm.toLowerCase()) ||
            (student.parent && (
                student.parent.first_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                student.parent.last_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                student.parent.email.toLowerCase().includes(searchTerm.toLowerCase())
            ))
        );
        
        if (filteredFamilyStudents.length > 0 || familyName.toLowerCase().includes(searchTerm.toLowerCase())) {
            acc[familyName] = filteredFamilyStudents;
        }
        return acc;
    }, {});
    
    $: filteredStudentsWithoutFamily = studentsWithoutFamily.filter(student => 
        student.first_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        student.last_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        student.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
        student.username.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (student.parent && (
            student.parent.first_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
            student.parent.last_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
            student.parent.email.toLowerCase().includes(searchTerm.toLowerCase())
        ))
    );
    
    // Format last login date
    function formatLastLogin(lastLoginStr) {
        if (!lastLoginStr) return 'Niekada';
        
        const lastLogin = new Date(lastLoginStr);
        const now = new Date();
        const timeDiff = now - lastLogin;
        const daysDiff = Math.floor(timeDiff / (1000 * 60 * 60 * 24));
        
        if (daysDiff === 0) return 'Šiandien';
        if (daysDiff === 1) return 'Vakar';
        if (daysDiff < 7) return `Prieš ${daysDiff} dienas`;
        if (daysDiff < 30) return `Prieš ${Math.floor(daysDiff / 7)} savaites`;
        if (daysDiff < 365) return `Prieš ${Math.floor(daysDiff / 30)} mėnesius`;
        
        return lastLogin.toLocaleDateString('lt-LT');
    }
    
    // Get status color based on last login
    function getLoginStatus(lastLoginStr) {
        if (!lastLoginStr) return 'never-logged';
        
        const lastLogin = new Date(lastLoginStr);
        const now = new Date();
        const daysDiff = Math.floor((now - lastLogin) / (1000 * 60 * 60 * 24));
        
        if (daysDiff <= 7) return 'recent';
        if (daysDiff <= 30) return 'moderate';
        return 'old';
    }
    
    // Fetch students data
    async function fetchStudents() {
        loading = true;
        error = '';
        
        try {
            const token = localStorage.getItem('jwtToken');
            if (!token) {
                error = 'Reikalinga autentifikacija. Prisijunkite prie sistemos.';
                return;
            }
            
            const response = await fetch('/studentplayground/api/students-with-info/', {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });
            
            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                error = errorData.message || 'Nepavyko gauti studentų duomenų';
                return;
            }
            
            const data = await response.json();
            if (data.status === 'success') {
                students = data.students;
                familyGroups = data.family_groups;
                studentsWithoutFamily = data.students_without_family;
            } else {
                error = data.message || 'Netikėta klaida';
            }
            
        } catch (err) {
            console.error('Error fetching students:', err);
            error = 'Tinklo klaida. Bandykite dar kartą.';
        } finally {
            loading = false;
        }
    }
    
    // Load data when component mounts
    onMount(() => {
        fetchStudents();
    });
</script>

<div class="students-list-container">
    <!-- Search and filters -->
    <div class="search-section">
        <div class="search-field">
            <label for="search">Ieškoti studentą:</label>
            <input 
                id="search" 
                type="text" 
                bind:value={searchTerm} 
                placeholder="Vardas, pavardė, el. paštas, tėvo vardas..."
            />
        </div>
        
        <div class="view-toggle">
            <label>
                <input type="checkbox" bind:checked={showGrouped} />
                Rodyti grupuotus pagal šeimas
            </label>
        </div>
    </div>
    
    <!-- Loading state -->
    {#if loading}
        <div class="loading-message">
            <p>Kraunami duomenys...</p>
        </div>
    {/if}
    
    <!-- Error state -->
    {#if error}
        <div class="error-message">
            <p>{error}</p>
            <button on:click={fetchStudents} class="retry-button">Bandyti dar kartą</button>
        </div>
    {/if}
    
    <!-- Students list -->
    {#if !loading && !error}
        <div class="students-summary">
            <p>Rasta studentų: <strong>{filteredStudents.length}</strong> iš {students.length}</p>
        </div>
        
        {#if showGrouped}
            <!-- Family groups view -->
            {#if Object.keys(filteredFamilyGroups).length > 0}
                <div class="family-groups">
                    {#each Object.entries(filteredFamilyGroups) as [familyName, familyStudents]}
                        <div class="family-group">
                            <div class="family-header">
                                <h3>🏠 {familyName}</h3>
                                <span class="family-count">{familyStudents.length} studentų</span>
                            </div>
                            
                            <div class="students-grid">
                                {#each familyStudents as student}
                                    <div class="student-card">
                                        <div class="student-header">
                                            <div class="student-name">
                                                <h4>{student.first_name} {student.last_name}</h4>
                                                <p class="student-email">{student.email}</p>
                                            </div>
                                            <div class="login-status {getLoginStatus(student.last_login)}">
                                                <span class="status-indicator"></span>
                                                {formatLastLogin(student.last_login)}
                                            </div>
                                        </div>
                                        
                                        <div class="student-details">
                                            <div class="detail-row">
                                                <span class="detail-label">Naudotojo vardas:</span>
                                                <span class="detail-value">{student.username}</span>
                                            </div>
                                            
                                            {#if student.phone_number}
                                                <div class="detail-row">
                                                    <span class="detail-label">Tel. numeris:</span>
                                                    <span class="detail-value">{student.phone_number}</span>
                                                </div>
                                            {/if}
                                            
                                            {#if student.parent}
                                                <div class="parent-section">
                                                    <h5>Tėvų informacija</h5>
                                                    <div class="detail-row">
                                                        <span class="detail-label">Tėvo vardas:</span>
                                                        <span class="detail-value">{student.parent.full_name}</span>
                                                    </div>
                                                    <div class="detail-row">
                                                        <span class="detail-label">Tėvo el. paštas:</span>
                                                        <span class="detail-value">{student.parent.email}</span>
                                                    </div>
                                                    {#if student.parent.phone_number}
                                                        <div class="detail-row">
                                                            <span class="detail-label">Tėvo tel.:</span>
                                                            <span class="detail-value">{student.parent.phone_number}</span>
                                                        </div>
                                                    {/if}
                                                </div>
                                            {/if}
                                            
                                            <div class="detail-row">
                                                <span class="detail-label">Registracijos data:</span>
                                                <span class="detail-value">{new Date(student.date_joined).toLocaleDateString('lt-LT')}</span>
                                            </div>
                                            
                                            <div class="detail-row">
                                                <span class="detail-label">Statusas:</span>
                                                <span class="detail-value {student.is_active ? 'active' : 'inactive'}">
                                                    {student.is_active ? 'Aktyvus' : 'Neaktyvus'}
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                {/each}
                            </div>
                        </div>
                    {/each}
                </div>
            {/if}
            
            <!-- Students without family -->
            {#if filteredStudentsWithoutFamily.length > 0}
                <div class="students-without-family">
                    <div class="section-header">
                        <h3>👤 Studentai be šeimos grupės</h3>
                        <span class="section-count">{filteredStudentsWithoutFamily.length} studentų</span>
                    </div>
                    
                    <div class="students-grid">
                        {#each filteredStudentsWithoutFamily as student}
                            <div class="student-card">
                                <div class="student-header">
                                    <div class="student-name">
                                        <h4>{student.first_name} {student.last_name}</h4>
                                        <p class="student-email">{student.email}</p>
                                    </div>
                                    <div class="login-status {getLoginStatus(student.last_login)}">
                                        <span class="status-indicator"></span>
                                        {formatLastLogin(student.last_login)}
                                    </div>
                                </div>
                                
                                <div class="student-details">
                                    <div class="detail-row">
                                        <span class="detail-label">Naudotojo vardas:</span>
                                        <span class="detail-value">{student.username}</span>
                                    </div>
                                    
                                    {#if student.phone_number}
                                        <div class="detail-row">
                                            <span class="detail-label">Tel. numeris:</span>
                                            <span class="detail-value">{student.phone_number}</span>
                                        </div>
                                    {/if}
                                    
                                    {#if student.parent}
                                        <div class="parent-section">
                                            <h5>Tėvų informacija</h5>
                                            <div class="detail-row">
                                                <span class="detail-label">Tėvo vardas:</span>
                                                <span class="detail-value">{student.parent.full_name}</span>
                                            </div>
                                            <div class="detail-row">
                                                <span class="detail-label">Tėvo el. paštas:</span>
                                                <span class="detail-value">{student.parent.email}</span>
                                            </div>
                                            {#if student.parent.phone_number}
                                                <div class="detail-row">
                                                    <span class="detail-label">Tėvo tel.:</span>
                                                    <span class="detail-value">{student.parent.phone_number}</span>
                                                </div>
                                            {/if}
                                        </div>
                                    {/if}
                                    
                                    <div class="detail-row">
                                        <span class="detail-label">Registracijos data:</span>
                                        <span class="detail-value">{new Date(student.date_joined).toLocaleDateString('lt-LT')}</span>
                                    </div>
                                    
                                    <div class="detail-row">
                                        <span class="detail-label">Statusas:</span>
                                        <span class="detail-value {student.is_active ? 'active' : 'inactive'}">
                                            {student.is_active ? 'Aktyvus' : 'Neaktyvus'}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        {/each}
                    </div>
                </div>
            {/if}
        {:else}
            <!-- Flat view -->
            <div class="students-list">
                {#each filteredStudents as student}
                    <div class="student-card">
                        <div class="student-header">
                            <div class="student-name">
                                <h4>{student.first_name} {student.last_name}</h4>
                                <p class="student-email">{student.email}</p>
                                {#if student.families.length > 0}
                                    <p class="student-families">🏠 {student.families.join(', ')}</p>
                                {/if}
                            </div>
                            <div class="login-status {getLoginStatus(student.last_login)}">
                                <span class="status-indicator"></span>
                                {formatLastLogin(student.last_login)}
                            </div>
                        </div>
                        
                        <div class="student-details">
                            <div class="detail-row">
                                <span class="detail-label">Naudotojo vardas:</span>
                                <span class="detail-value">{student.username}</span>
                            </div>
                            
                            {#if student.phone_number}
                                <div class="detail-row">
                                    <span class="detail-label">Tel. numeris:</span>
                                    <span class="detail-value">{student.phone_number}</span>
                                </div>
                            {/if}
                            
                            {#if student.parent}
                                <div class="parent-section">
                                    <h5>Tėvų informacija</h5>
                                    <div class="detail-row">
                                        <span class="detail-label">Tėvo vardas:</span>
                                        <span class="detail-value">{student.parent.full_name}</span>
                                    </div>
                                    <div class="detail-row">
                                        <span class="detail-label">Tėvo el. paštas:</span>
                                        <span class="detail-value">{student.parent.email}</span>
                                    </div>
                                    {#if student.parent.phone_number}
                                        <div class="detail-row">
                                            <span class="detail-label">Tėvo tel.:</span>
                                            <span class="detail-value">{student.parent.phone_number}</span>
                                        </div>
                                    {/if}
                                </div>
                            {/if}
                            
                            <div class="detail-row">
                                <span class="detail-label">Registracijos data:</span>
                                <span class="detail-value">{new Date(student.date_joined).toLocaleDateString('lt-LT')}</span>
                            </div>
                            
                            <div class="detail-row">
                                <span class="detail-label">Statusas:</span>
                                <span class="detail-value {student.is_active ? 'active' : 'inactive'}">
                                    {student.is_active ? 'Aktyvus' : 'Neaktyvus'}
                                </span>
                            </div>
                        </div>
                    </div>
                {/each}
            </div>
        {/if}
        
        {#if filteredStudents.length === 0 && students.length > 0}
            <div class="no-results">
                <p>Nerasta studentų pagal jūsų paieškos kriterijus.</p>
            </div>
        {:else if students.length === 0}
            <div class="no-students">
                <p>Sistemoje nėra registruotų studentų.</p>
            </div>
        {/if}
    {/if}
</div>

<style>
    .students-list-container {
        max-width: 100%;
        margin: 0;
        padding: 0;
    }
    
    .search-section {
        background: #f8f9fa;
        padding: 15px;
        border-radius: 8px;
        margin-bottom: 20px;
        border: 1px solid #e1e5e9;
        display: flex;
        gap: 20px;
        align-items: flex-end;
    }
    
    .search-field {
        flex: 1;
    }
    
    .search-field label {
        display: block;
        margin-bottom: 8px;
        font-weight: 500;
        color: #333;
    }
    
    .search-field input {
        width: 100%;
        padding: 12px 16px;
        border: 2px solid #e1e5e9;
        border-radius: 8px;
        font-size: 16px;
        transition: border-color 0.3s ease;
    }
    
    .search-field input:focus {
        outline: none;
        border-color: #667eea;
        box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
    }
    
    .view-toggle {
        display: flex;
        align-items: center;
        gap: 8px;
        font-weight: 500;
        color: #333;
    }
    
    .view-toggle input[type="checkbox"] {
        transform: scale(1.2);
    }
    
    .loading-message, .error-message, .no-results, .no-students {
        text-align: center;
        padding: 40px 20px;
        background: white;
        border-radius: 12px;
        box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
    }
    
    .error-message {
        color: #dc3545;
    }
    
    .retry-button {
        background: #667eea;
        color: white;
        border: none;
        padding: 10px 20px;
        border-radius: 6px;
        cursor: pointer;
        margin-top: 15px;
        transition: background-color 0.3s ease;
    }
    
    .retry-button:hover {
        background: #5a67d8;
    }
    
    .students-summary {
        margin-bottom: 20px;
        padding: 15px;
        background: #f8f9fa;
        border-radius: 8px;
        text-align: center;
    }
    
    /* Family Groups Styles */
    .family-groups {
        display: flex;
        flex-direction: column;
        gap: 30px;
    }
    
    .family-group {
        background: white;
        border-radius: 12px;
        padding: 20px;
        box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
        border: 2px solid #e1e5e9;
    }
    
    .family-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: 20px;
        padding-bottom: 15px;
        border-bottom: 2px solid #f0f4f8;
    }
    
    .family-header h3 {
        margin: 0;
        color: #2d3748;
        font-size: 1.4em;
        font-weight: 600;
    }
    
    .family-count, .section-count {
        background: #fab45a;
        color: white;
        padding: 6px 12px;
        border-radius: 15px;
        font-size: 0.85em;
        font-weight: 500;
    }
    
    .students-without-family {
        background: white;
        border-radius: 12px;
        padding: 20px;
        box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
        border: 2px solid #e1e5e9;
        margin-top: 30px;
    }
    
    .section-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: 20px;
        padding-bottom: 15px;
        border-bottom: 2px solid #f0f4f8;
    }
    
    .section-header h3 {
        margin: 0;
        color: #2d3748;
        font-size: 1.4em;
        font-weight: 600;
    }
    
    /* Students Grid */
    .students-grid {
        display: grid;
        grid-template-columns: repeat(auto-fill, minmax(400px, 1fr));
        gap: 20px;
    }
    
    .students-list {
        display: grid;
        gap: 20px;
    }
    
    .student-card {
        background: #fafbfc;
        border-radius: 10px;
        padding: 20px;
        box-shadow: 0 1px 5px rgba(0, 0, 0, 0.05);
        border: 1px solid #e8ecf0;
        transition: transform 0.2s ease, box-shadow 0.2s ease;
    }
    
    .student-card:hover {
        transform: translateY(-2px);
        box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
    }
    
    .student-header {
        display: flex;
        justify-content: space-between;
        align-items: flex-start;
        margin-bottom: 15px;
        padding-bottom: 12px;
        border-bottom: 1px solid #e1e5e9;
    }
    
    .student-name h4 {
        margin: 0 0 5px 0;
        color: #2d3748;
        font-size: 1.1em;
        font-weight: 600;
    }
    
    .student-email {
        margin: 0;
        color: #667eea;
        font-size: 0.9em;
    }
    
    .student-families {
        margin: 5px 0 0 0;
        color: #fab45a;
        font-size: 0.85em;
        font-weight: 500;
    }
    
    .login-status {
        display: flex;
        align-items: center;
        gap: 6px;
        padding: 6px 10px;
        border-radius: 15px;
        font-size: 0.8em;
        font-weight: 500;
    }
    
    .status-indicator {
        width: 6px;
        height: 6px;
        border-radius: 50%;
    }
    
    .login-status.recent {
        background: #d4edda;
        color: #155724;
    }
    
    .login-status.recent .status-indicator {
        background: #28a745;
    }
    
    .login-status.moderate {
        background: #fff3cd;
        color: #856404;
    }
    
    .login-status.moderate .status-indicator {
        background: #ffc107;
    }
    
    .login-status.old {
        background: #f8d7da;
        color: #721c24;
    }
    
    .login-status.old .status-indicator {
        background: #dc3545;
    }
    
    .login-status.never-logged {
        background: #e2e3e5;
        color: #383d41;
    }
    
    .login-status.never-logged .status-indicator {
        background: #6c757d;
    }
    
    .student-details {
        display: grid;
        gap: 10px;
    }
    
    .detail-row {
        display: grid;
        grid-template-columns: 1fr 1fr;
        gap: 10px;
        align-items: center;
    }
    
    .detail-label {
        font-weight: 500;
        color: #4a5568;
        font-size: 0.9em;
    }
    
    .detail-value {
        color: #2d3748;
        word-break: break-word;
        font-size: 0.9em;
    }
    
    .detail-value.active {
        color: #28a745;
        font-weight: 500;
    }
    
    .detail-value.inactive {
        color: #dc3545;
        font-weight: 500;
    }
    
    .parent-section {
        background: #f0f4f8;
        padding: 12px;
        border-radius: 8px;
        margin-top: 8px;
        grid-column: 1 / -1;
    }
    
    .parent-section h5 {
        margin: 0 0 10px 0;
        color: #2d3748;
        font-size: 0.95em;
        font-weight: 600;
    }
    
    .parent-section .detail-row {
        margin-bottom: 5px;
    }
    
    .parent-section .detail-row:last-child {
        margin-bottom: 0;
    }
    
    @media (max-width: 768px) {
        .search-section {
            flex-direction: column;
            align-items: stretch;
        }
        
        .students-grid {
            grid-template-columns: 1fr;
        }
        
        .student-header {
            flex-direction: column;
            gap: 15px;
        }
        
        .detail-row {
            grid-template-columns: 1fr;
            gap: 5px;
        }
        
        .detail-label {
            font-size: 0.8em;
        }
        
        .family-header, .section-header {
            flex-direction: column;
            gap: 10px;
            align-items: flex-start;
        }
    }
</style>
